import {getTestProps} from '@benchling/js-test-utils';
import {Checkbox, createStyles, TextInput} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {useEntityMapSelector} from 'registry/entity-map/EntityMapMachineProvider';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import sortedBy from 'util/sortedBy';

const SIDEBAR_WIDTH = 230;
const ICON_BAR_WIDTH = 40;

const useStyles = createStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
  },
  iconBar: {
    boxSizing: 'border-box',
    width: ICON_BAR_WIDTH,
    minWidth: ICON_BAR_WIDTH,
    height: '100%',
    background: theme.white,
    borderRight: `1px solid ${theme.colors.gray[2]}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 0,
    paddingTop: theme.spacing.sm,
  },
  iconButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing.sm,
    borderRadius: theme.radius.md,
    color: theme.colors.gray[6],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  iconButtonActive: {
    color: theme.colors.blue[6],
    backgroundColor: theme.colors.blue[0],
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
    },
  },
  sidebar: {
    boxSizing: 'border-box',
    width: SIDEBAR_WIDTH,
    minWidth: SIDEBAR_WIDTH,
    height: '100%',
    borderRight: `1px solid ${theme.colors.gray[2]}`,
    background: theme.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 0,
    gap: theme.spacing.lg,
    overflow: 'hidden',
  },
  header: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    alignSelf: 'stretch',
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flex: 1,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: theme.fontSizes.md,
    lineHeight: '16px',
    color: theme.colors.gray[9],
    flex: 1,
  },
  collapseButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.gray[6],
    fontSize: 16,
    lineHeight: 1,
    '&:hover': {
      opacity: 0.7,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: `0 ${theme.spacing.md}`,
    gap: theme.spacing.md,
    alignSelf: 'stretch',
    flex: 1,
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  sectionLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    lineHeight: '14px',
    textTransform: 'uppercase' as const,
    color: theme.colors.gray[9],
  },
  selectAllButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: theme.fontSizes.md,
    fontWeight: 400,
    lineHeight: '16px',
    color: theme.colors.blue[3],
    padding: 0,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  searchInput: {
    alignSelf: 'stretch',
    '& .mantine-TextInput-input': {
      height: 24,
      minHeight: 24,
      borderRadius: theme.radius.md,
      border: `1px solid ${theme.colors.gray[2]}`,
      fontSize: theme.fontSizes.md,
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .mantine-TextInput-icon': {
      color: theme.colors.gray[3],
    },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
    flex: 1,
    overflowY: 'auto',
    alignSelf: 'stretch',
  },
}));

interface EntityMapSchemaFilterProps {
  hiddenSchemas: Set<string>;
  onChangeHiddenSchemas: (hiddenSchemas: Set<string>) => void;
}

export default function EntityMapSchemaFilter({
  hiddenSchemas,
  onChangeHiddenSchemas,
}: EntityMapSchemaFilterProps): JSX.Element | null {
  const {classes, cx} = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const graphNodesById = useEntityMapSelector((s) => s.context.graphNodesById);

  const schemaNames = useMemo(() => {
    const names = new Set<string>();
    for (const node of graphNodesById.values()) {
      if (!isInaccessible(node.data) && node.data.schemaName != null) {
        names.add(node.data.schemaName);
      }
    }
    return sortedBy(Array.from(names), [(name: string) => name]);
  }, [graphNodesById]);

  if (schemaNames.length <= 1) {
    return null;
  }

  const filteredSchemas = searchQuery
    ? schemaNames.filter((name) => name.toLowerCase().includes(searchQuery.toLowerCase()))
    : schemaNames;

  const handleToggleSchema = (schemaName: string): void => {
    const next = new Set(hiddenSchemas);
    if (next.has(schemaName)) {
      next.delete(schemaName);
    } else {
      next.add(schemaName);
    }
    onChangeHiddenSchemas(next);
  };

  const handleSelectAll = (): void => {
    onChangeHiddenSchemas(new Set());
  };

  const handleDeselectAll = (): void => {
    onChangeHiddenSchemas(new Set(schemaNames));
  };

  const allSelected = hiddenSchemas.size === 0;

  return (
    <div className={classes.wrapper} {...getTestProps({component: 'EntityMapSchemaFilter', element: 'root'})}>
      <div className={classes.iconBar}>
        <button
          type="button"
          className={cx(classes.iconButton, expanded && classes.iconButtonActive)}
          onClick={() => {
            setExpanded((v) => !v);
          }}
          aria-label="Toggle filters"
          {...getTestProps({component: 'EntityMapSchemaFilter', element: 'toggle-button'})}
        >
          <BenchlingIcon icon="filter-menu" />
        </button>
      </div>
      {expanded && (
        <div className={classes.sidebar}>
          <div className={classes.header}>
            <div className={classes.headerInner}>
              <span className={classes.headerTitle}>Filters</span>
              <button
                type="button"
                className={classes.collapseButton}
                onClick={() => {
                  setExpanded(false);
                }}
                aria-label="Collapse filters"
                {...getTestProps({component: 'EntityMapSchemaFilter', element: 'collapse-button'})}
              >
                <BenchlingIcon icon="chevron-left" />
              </button>
            </div>
          </div>
          <div className={classes.content}>
            <div className={classes.sectionHeader}>
              <span className={classes.sectionLabel}>Entity Schemas</span>
              <button
                type="button"
                className={classes.selectAllButton}
                onClick={allSelected ? handleDeselectAll : handleSelectAll}
                {...getTestProps({component: 'EntityMapSchemaFilter', element: 'select-all'})}
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <div className={classes.searchInput}>
              <TextInput
                placeholder="Search"
                aria-label="Search schemas"
                size="xs"
                icon={<BenchlingIcon icon="search" />}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.currentTarget.value);
                }}
                {...getTestProps({component: 'EntityMapSchemaFilter', element: 'search'})}
              />
            </div>
            <div className={classes.list}>
              {filteredSchemas.map((name) => (
                <Checkbox
                  key={name}
                  label={name}
                  checked={!hiddenSchemas.has(name)}
                  onChange={() => {
                    handleToggleSchema(name);
                  }}
                  {...getTestProps({component: 'EntityMapSchemaFilter', element: `schema-${name}`})}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
